/*global $:false */

import { Keywords, extendDef, parseOptions } from '../fields/recordm.fields.js';

import BaseM from './recordm.model.entity.js';
import UriBuilder from '../helpers/module.uribuilder.js';
import { getIndexFieldName } from "../helpers/recordm.value-formatter";
import { sprintf } from 'sprintf-js';

import ui from '@cob/ui-genesis/cob.ui.js';
import utils from '@cob/ui-genesis/cob.utils.js';

//Model responsible for configuring interacting with a definition;
var DefinitionM = function(core, data) {
    /* eslint-disable-next-line prefer-rest-params */
    BaseM.apply(this, arguments);

    this.data = data || {id: undefined};

    this._i18nKey = "recordm.definition";
    this._i18nPrefix = "definition";
    this._entityKey = DefinitionM.entityKey;

    this.created_event_name = DefinitionM.created_event_name;

    this.loaded_event_name = "recordm:loaded:definition:" + this.data.id;
    this.update_event_name = "recordm:updated:definition:" + this.data.id;
    this.delete_event_name = "recordm:deleted:definition:" + this.data.id;
    this.enable_event_name = "recordm:enable:definition:" + this.data.id;
    this.disable_event_name = "recordm:disable:definition:" + this.data.id;
};

DefinitionM.entityKey = 'definitions';
DefinitionM.created_event_name = 'recordm:created:definition';

DefinitionM.prototype = new BaseM();
DefinitionM.prototype.constructor = DefinitionM;

// The getter for a specific definition
DefinitionM.get = BaseM.buildGetFor(DefinitionM);

DefinitionM.getByName = function(core, definitionName, onSuccessCb, onErrorCb){
    const endpointPath = 'recordm/definitions/name/' + definitionName;

    $.ajax({
        url: endpointPath,
        type: 'GET',
        dataType: 'json',
        accepts: "application/json",
        xhrFields: {
            withCredentials: true
        },
        success: function(data) {
            var entityM = new DefinitionM(core, data);

            core.add_and_subscribe(entityM.getEntityKey() + ":" + data.id, entityM);

            core.publish(entityM.loaded_event_name, entityM);

            if (onSuccessCb) onSuccessCb(entityM);
        },
        error: function(req) {
            window.console.log("ENTITY:" + DefinitionM.entityKey + ":error: " +
                               req.status + " : " + req.statusText + ":" + req.responseText);

            if (onErrorCb) onErrorCb(req.status, req);
        }
    });
};

// Get all the definitions but with no fields.
// This is useful only for domain / definition management.
DefinitionM.loadAll = function(core, callback) {

    $.ajax({
        'url': 'recordm/definitions?includeDisabled=true',
        'type': 'get',
        success: function(definitions) {
            var definitionIds = [];

            definitions.forEach(function(definition) {
                var definitionM = new DefinitionM(core, definition),
                    definitionId = definition.id;

                core.addModel(definitionM.getEntityKey() + ":" + definitionId, definitionM);
                definitionIds.push(definitionId);
            });

            if (callback) callback(definitionIds);
        },
        error: function(req) {
            window.console.log("DEFINITIONS:error: " + req.status + " : " + req.statusText + ":" + req.responseText);
        }
    });
};

// Sort the definitions.
// The algorithm will sort the definitions by name and state.
// At the top will be all enabled definitions.
DefinitionM.sortByNameAndStateFn = function(definitionM, otherDefinitionM) {
    var compA = definitionM.getName().toUpperCase();
    var compB = otherDefinitionM.getName().toUpperCase();

    if (definitionM.getState() === otherDefinitionM.getState()) {
        return compA.localeCompare(compB);

    } else if (definitionM.isEnabled()) {
        return -1;
    } else {
        return 1;
    }
};

DefinitionM.columns = [
    BaseM._buildEntityField("name", {
        label: "Nome",
        columnWidth: 260,
        type: BaseM.fieldDataType.STRING_PHRASE
    }),
    BaseM._buildEntityField("description", {
        label: "Description",
        columnWidth: 260,
        type: BaseM.fieldDataType.STRING_PHRASE
    })
];

// it's all done on the server
DefinitionM.fieldValidationProps = []

// Return the definition id
DefinitionM.prototype.getId = function() {
    return this.data.id;
};

// Return the definition name
DefinitionM.prototype.getName = function() {
    return this.data.name;
};

// Return the definition description
DefinitionM.prototype.getDescription = function() {
    return this.data.description || "";
};

// Returns true if the user can update this definition
DefinitionM.prototype.canUpdate = function() {
    return this.data._links && this.data._links.update;
};

// Returns true if the user can update this definition
DefinitionM.prototype.canChangeState = function() {
    return this.data._links
        && (this.data._links.enable || this.data._links.disable);
};

// Returns true if the user can clone this definition
DefinitionM.prototype.canClone = function() {
    return this.data._links && this.data._links.clone;
};

// Returns true if the user can clone this definition
DefinitionM.prototype.canDelete = function() {
    return this.data._links && this.data._links['delete'];
};

// Returns true if the user can perform bulk operations
// on instances of this definition
DefinitionM.prototype.canCreateBulkRequests = function() {
    return this.data._links && this.data._links.bulkRequest;
};

// returns the definition state
//
// Possible return values:
// * 'enabled'
// * 'disabled'
DefinitionM.prototype.getState = function() {
    return this.data.state;
};

// returns true if the definition is enabled
DefinitionM.prototype.isEnabled = function() {
    return this.data.state === "enabled";
};

// Returns a unique setting key that will be used to save the settings associated
// to this definition.
DefinitionM.prototype.getSettingKey = function() {
    return this._entityKey + "-" + this.data.id;
};

// Indexes a definition
// This will submit an indexJob for this definition
DefinitionM.prototype.index = function(recreate) {
    $.ajax({
        'url': 'recordm/jobs',
        'type': 'post',
        contentType: "application/json",
        data: JSON.stringify({
            name: "indexJob",
            parameters: {
                "definitions": [ String(this.data.id) ],
                "bulk": true,
                "recreateIndex": recreate || false
            }
        }),
        success: function() {
            ui.notification.showInfo("Indexing has started.");
        },
        error: function(req) {
            window.console.log("DEFINITIONS:error: " + req.status + " : " + req.statusText + ":" + req.responseText);

            ui.notification.showNotificationsFromRequest('error', req);
        }
    });
};

// Upgrades a Definition's Instances
// This will submit an updateDefinitionInstancesJob
DefinitionM.prototype.upgradeInstances = function() {
    $.ajax({
        'url': 'recordm/jobs',
        'type': 'post',
        contentType: "application/json",
        data: JSON.stringify({
            name: "updateDefinitionInstancesJob",
            parameters: {
                "definitions": [ String(this.data.id) ],
                "definitionName": this.data.name
            }
        }),
        success: function() {
            ui.notification.showInfo("Upgrading instances has started.");
        },
        error: function(req) {
            window.console.log("DEFINITIONS:error: " + req.status + " : " + req.statusText + ":" + req.responseText);

            ui.notification.showNotificationsFromRequest('error', req);
        }
    });
};

// Create a clone of this definition
// This will send the `create_event_name` on success
DefinitionM.prototype.clone = function() {
    var _this = this;

    $.ajax({
        'url': _this.data._links.clone,
        'type': 'post',
        success: function(clonedDefinition) {
            var definitionM = new DefinitionM(_this._core, clonedDefinition);
            _this._core.addModel(definitionM.getEntityKey() + ":" + definitionM.data.id, definitionM);

            _this._core.publish(DefinitionM.created_event_name,
                {id: definitionM.data.id, definitionM: definitionM}
            );
        },
        error: function(req) {
            window.console.log("DEFINITIONS:error: " + req.status + " : " + req.statusText + ":" + req.responseText);

            if (req.status === 404) {
                ui.notification.showCustom(
                    'error',
                    sprintf(_this._core.translateString("recordm.definition", "definition.msg.error-title"), _this.getName()),
                    _this._core.translateString("recordm.domains", "definitions.msg.not-found")
                );
            } else {
                ui.notification.showNotificationsFromRequest('error', req);
            }
        }
    });
};

// Disable this definition.
// This will send the `disable_event_name` on success
DefinitionM.prototype.disable = function() {
    var _this = this;

    $.ajax({
        url: _this.data._links.disable,
        type: 'put',
        success: function(definition) {
            _this.data = definition;

            _this._core.publish(_this.disable_event_name, {id: _this.data.id, definitionM: _this});
        },
        error: function(req) {
            try {
                window.console.log("DEFINITIONS:error: " + req.status + " : " + req.statusText + ":" + req.responseText);

                if (req.status === 404) {
                    ui.notification.showCustom(
                        'error',
                        sprintf(_this._core.translateString("recordm.definition", "definition.msg.error-title"), _this.getName()),
                        _this._core.translateString("recordm.domains", "definitions.msg.not-found")
                    );
                } else {
                    ui.notification.showNotificationsFromRequest('error', req);
                }


            } catch (e) {
                window.console.log(e);
            }
        }
    });
};

// Enable this definition
// This will send the `enable_event_name` on success
DefinitionM.prototype.enable = function() {
    var _this = this;

    $.ajax({
        url: _this.data._links.enable,
        type: 'put',
        success: function(definition) {
            _this.data = definition;

            _this._core.publish(_this.enable_event_name, {id: _this.data.id, definitionM: _this});
        },
        error: function(req) {
            try {
                window.console.log("DEFINITIONS:error: " + req.status + " : " + req.statusText + ":" + req.responseText);

                if (req.status === 404) {
                    ui.notification.showCustom(
                        'error',
                        sprintf(_this._core.translateString("recordm.definition", "definition.msg.error-title"), _this.getName()),
                        _this._core.translateString("recordm.domains", "definitions.msg.not-found")
                    );
                } else {
                    ui.notification.showNotificationsFromRequest('error', req);
                }

            } catch (e) {
                window.console.log(e);
            }
        }
    });
};

DefinitionM.prototype.getExportLink = function(){
    return this.data._links['export'];
};

// Should return all definition fields from this definition to be used
// in the definition specific search.
//
// receives an opts object with the following props:
// excludeLinks:true|false   -> allows to exclude $link and $create fields in the collection
//                              (along with $group fields which are already excluded)
//
// Returns an array with objects build from `BaseM._buildEntityField`.
// Ex:
// ```
//   BaseM._buildEntityField("id", {
//        label: "ID",
//        columnWidth: 50,
//        resizable: false,
//        visibleByDefault: true,
//        type: BaseM.fieldDataType.NUMBER,
//        formatter: function(value, context) {
//            var uri = recordm.UriBuilder.buildUriForInstance(context.id);
//            return value
//                ? '<a href="#/' + uri + '" class="link normal-fontsize">' + value + '</a>'
//                : value;
//        }
//   })
// ```
DefinitionM.prototype.getSearchableFields = function(opts) {
    var _this = this,
        _opts = opts||{};

    // Start by adding the instance id.
    var propsMap = {
        id: BaseM._buildEntityField("id", {
            label: "ID",
            columnWidth: 20,
            visibleByDefault: true,
            align: "right",
            type: BaseM.fieldDataType.STRING,
            formatter: function(value, context) {
                var uri = UriBuilder.buildUriForInstance(context.id);
                return value
                    ? '<a href="#/' + uri + '" class="link normal-fontsize js-field-id-cell" data-entityId="' + value + '">' + value + '</a>'
                    : value;
            }
        })
    };
    const fields = _this._getFieldsFlatten()
    fields.forEach((fd) => extendDef(fd));

    fields.forEach(function(fieldDefinition) {
        var inputField,
            fieldId = getIndexFieldName(fieldDefinition.name),
            fieldProps,
            existingFieldProps,
            newOptions;

       inputField = !(/\$group(\s+|$)/).test(fieldDefinition.description) // not a $group
          && !(/\$references/).test(fieldDefinition.description) // not a $references
          && !(_opts.excludeLinks && (/\$link(\s+|$)|\$create\(.*/).test(fieldDefinition.description)); // not a $link if _opts.excludeLinks

        // RecordM allows an instance to have multiple fields with the same name but different types.
        // For every field that is neither a $group or a $[], we only use the first field with a specific name (ignoring the others).
        // If there are multiple $[] fields with the same name, their possible values are merged into a single list.
        if (inputField){
            fieldProps = _this._buildSearchableFieldProp(fieldDefinition);
            existingFieldProps = propsMap[fieldId];

            if(existingFieldProps === undefined) {
                propsMap[fieldId] = fieldProps;

            } else {
               if(existingFieldProps.type === fieldProps.type
                   && existingFieldProps.type === BaseM.fieldDataType.MULTIVALUE){

                   newOptions = fieldProps.possibleValues.filter(function(value){
                       return existingFieldProps.possibleValues.indexOf(value) === -1;
                   });

                   existingFieldProps.possibleValues = existingFieldProps.possibleValues
                       .concat(newOptions)
                       .sort(function(val1, val2) {
                           return val1.toLowerCase().localeCompare(val2.toLowerCase());
                       });
               }
            }
        }
    });

    fields
        .filter((fd) => fd.isA(Keywords.References) && fd.conf(Keywords.References).args?.aggs)
        .map((fd) => {
           const args = fd.conf(Keywords.References).args
           const aggs = parseOptions(args.aggs);

           Object.keys(aggs).forEach((k) => {
              const conf = _this._buildSearchableFieldProp(fd);
              conf.label =  k;
              conf.fieldName = getIndexFieldName(k);
              conf.type = BaseM.fieldDataType.NUMBER;

              propsMap[conf.fieldName] = conf;
           })
        });

    // convert to an array and return
    var fieldsProps = [];

    utils.forEachOwn(propsMap, function(key) {
        fieldsProps.push(propsMap[key]);
    });

    return  fieldsProps;
};

// Returns a specific field from the definition
// args:
// * `fieldName`: the field name
DefinitionM.prototype.getSearchableField = function(fieldName) {
    var _column;

    this.getSearchableFields().some(function(field) {
        if (field.fieldName === fieldName) {
            _column = field;
            return true;
        }

        return false;
    });

    return _column;
};

//
// Helper functions
//

// Return all the field definitions belonging to this definition in a flat structure.
DefinitionM.prototype._getFieldsFlatten = function() {

    var _getChildFields = function(definitions, filter) {
        var fieldsCollection = [];

        definitions.forEach(function(def) {
            if (filter && typeof filter == "function" && filter(def)) {
                fieldsCollection.push(def);

                if (def.fields && def.fields.length > 0) {
                    fieldsCollection = fieldsCollection.concat(_getChildFields(def.fields, filter));
                }
            }
        });

        return fieldsCollection;
    };

    var _filter = function(it){
        //todo jone ligar perms
        // exclude $restricted fields
        return !(( /\$restricted\(/ ).test(it.description));
    };

    return _getChildFields(this.data.fieldDefinitions, _filter);
};

// Build the search property for a field definition with all the necessary properties defined.
DefinitionM.prototype._buildSearchableFieldProp = function(fieldDefinition) {
    var shouldBeVisible = (( /(\$instanceLabel|\$instanceDescription)/ ).test(fieldDefinition.description)),
        fieldId = getIndexFieldName(fieldDefinition.name);

    var fieldProp = BaseM._buildEntityField(fieldId, {
        label: fieldDefinition.name,
        columnWidth: 150,
        visibleByDefault: shouldBeVisible,
        align: "left",
        type: BaseM.fieldDataType.STRING,
        fieldDefDescription: fieldDefinition.description,
        fieldDefId: fieldDefinition.id,
        formatter: function(value, dataContext, columnDef, locale) {
            return BaseM.getReadableFieldValue(dataContext, columnDef.name,
                columnDef.fieldDefDescription, columnDef.fieldDefId, locale);
        }
    });

    if (( /\$datetime/ ).test(fieldDefinition.description)) {
        // date field
        this._addDateTimeProperties(fieldProp);

    } else if (( /\$date/ ).test(fieldDefinition.description)) {
        // date field
        this._addDateProperties(fieldProp);

    } else if (( /\$number/ ).test(fieldDefinition.description)) {
        // number field
        this._addNumberProperties(fieldProp);

    } else if (( /\$ref\(/ ).test(fieldDefinition.description) || ( /\$extRef/ ).test(fieldDefinition.description)) {
        // Add a formatter pointing to the referenced field
        this._addRefProperties(fieldProp);

    } else if (( /\$\[([^\]]+)\]/ ).exec(fieldDefinition.description)) {
        // select field
        this._addOptionsProperties(fieldProp,fieldDefinition);

    } else if (( /\$tasks/ ).exec(fieldDefinition.description)) {
        this._addTasksProperties(fieldProp);

    }

    return fieldProp;
};

DefinitionM.prototype._addDateProperties = function(fieldProperty) {
    fieldProperty.columnWidth = 80;
    fieldProperty.align = "center";
    fieldProperty.type = BaseM.fieldDataType.DATE;
};

DefinitionM.prototype._addDateTimeProperties = function(fieldProperty) {
    fieldProperty.columnWidth = 80;
    fieldProperty.align = "center";
    fieldProperty.type = BaseM.fieldDataType.DATETIME;
};

DefinitionM.prototype._addNumberProperties = function(fieldProperty) {
    fieldProperty.align = "right";
    fieldProperty.type = BaseM.fieldDataType.NUMBER;
};

DefinitionM.prototype._addOptionsProperties = function(fieldProperty, fieldDefinition) {
    var options = (/\$\[([^\]]+)\]/).exec(fieldDefinition.description);

    fieldProperty.type = BaseM.fieldDataType.MULTIVALUE;
    fieldProperty.possibleValues = options[1].split(",").map(function(option) {
        var opt = option.trim();
        if (opt.charAt(0) === '*') {
            opt = opt.substr(1);
        }

        return opt;
    }).sort(function(val1, val2) {
            return val1.toLowerCase().localeCompare(val2.toLowerCase());
    });

    fieldProperty.formatter = function(value, dataContext, columnDef) {
        if (value) {
            // don't pass the fieldDefDescription so that BaseM.getReadableFieldValue only joins all the values
            return BaseM.getReadableFieldValue(dataContext, columnDef.name);
        }

        return "";
    };
};

DefinitionM.prototype._addRefProperties = function(fieldProperty) {
    // Add a "_reference" suffix to the property's fieldName so that we can search for the $instanceLabel and not the id
    fieldProperty.fieldName += "_reference";
};

DefinitionM.prototype._addTasksProperties = function(fieldProperty) {
    fieldProperty.type = BaseM.fieldDataType.TASKS;
};

DefinitionM.prototype.buildUriForQuery = function(query) {
    return UriBuilder.buildUriForSimpleSearchDefinition(this.getId(), query);
};

DefinitionM.prototype.buildUriForAdvSearch = function(query) {
    return UriBuilder.buildUriForAdvSearchDefinition(this.getId(), query);
};

export default DefinitionM;
